import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ArrowRight, Check, Leaf, Sparkle, X } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import HeroDiagram from '../components/landing/HeroDiagram';
import { ReadingProgressBar } from '../components/landing/ReadingProgressBar';
import HowItWorksSteps from '../components/landing/HowItWorksSteps';
import ExtractionFlow from '../components/landing/ExtractionFlow';
import { ComparisonMatrix } from '../components/landing/ComparisonMatrix';
import { PlatformComparisonTable } from '../components/landing/PlatformComparisonTable';
import { ArchenFitSection } from '../components/landing/ArchenFitSection';
import { getLandingPage } from '../data/landingPages';
import { setCanonical, setBreadcrumbJsonLd, setRobotsMeta } from '../lib/seo';

// Desi-approved for this deploy (2026-08-25): sitemap + nav swap done. Everything
// else (lp-006, lp-011, lp-012, lp-013) stays on its /mockup/ path.
const GRADUATED = {
  'lp-001': '/zero-entry-project-management',
  'lp-002': '/interior-design-project-management-software',
  'lp-003': '/interior-design-client-approval-software',
  'lp-004': '/interior-design-client-communication-software',
  'lp-005': '/client-portal-software-interior-designers',
  'lp-007': '/project-memory-software',
  'lp-008': '/design-decision-documentation-software',
  'lp-009': '/design-firm-knowledge-management',
  'lp-010': '/automatic-project-documentation',
};

// Explicitly deferred as a business decision (not a content issue): held out of
// the sitemap and nav, and noindexed since they're still reachable via
// cross-links from other LP pages.
const DEFERRED = new Set(['lp-011', 'lp-012', 'lp-013']);

// Config-driven landing-page renderer. One template, many pages (see data/landingPages.js).
//
// SCALING RULE (do not break): never interpolate a variable into a Tailwind class
// (e.g. `w-[${x}px]` or `grid-cols-[...${x}...]`). Tailwind purges those at build time
// and the class silently does nothing. For any DYNAMIC value use an inline `style`
// (see SplitSection flexBasis, hero maxWidth, IMAGE_FADE). Static arbitrary values are
// fine only when written as complete literal strings.

const EYEBROW = 'text-[#7A5A2E] text-[11px] tracking-[0.2em] uppercase';
const H2 = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 };
const SERIF = "'Cormorant Garamond', serif";
const BG = { cream: 'bg-[#FDF8F3]', beige: 'bg-[#F5EFE6]' };

// Shared layout tokens: single source of truth for page rhythm.
// SECTION = vertical padding every section shares. CONTAINER = centered content
// column + gutters. EDGE_GUTTER = right/left pad on bleed sections so the copy
// column lines up with CONTAINER's edge while the image runs to the screen edge.
const CONTAINER = 'max-w-[1240px] mx-auto px-6 md:px-10';
const SECTION = 'py-20 md:py-28';
const padY = (s) => ((s.pt || s.pb) ? `${s.pt || 'pt-20 md:pt-28'} ${s.pb || 'pb-20 md:pb-28'}` : SECTION);
const EDGE_GUTTER = 'max(1.5rem, calc((100vw - 1240px) / 2 + 2.5rem))';
const DEFAULT_TEXT_W = 440;

// Single source of truth for the soft product-shot treatment: feathers all four edges
// so the shot melts into the page instead of ending on a hard rectangle/shadow.
// Reused by the hero overlay and any section image with `image.fade`.
const FEATHER = 'linear-gradient(to right, transparent 0, #000 2.5%, #000 97.5%, transparent 100%), linear-gradient(to bottom, transparent 0, #000 2.5%, #000 97.5%, transparent 100%)';
const IMAGE_FADE = {
  WebkitMaskImage: FEATHER,
  WebkitMaskComposite: 'source-in',
  maskImage: FEATHER,
  maskComposite: 'intersect',
};

const BOTTOM_FADE_GRAD = 'linear-gradient(to bottom, black calc(100% - 48px), transparent 100%)';
const BOTTOM_FADE = { WebkitMaskImage: BOTTOM_FADE_GRAD, maskImage: BOTTOM_FADE_GRAD };

// Single-edge feathers for full-bleed images: the outer (screen) edge dissolves
// into the page background instead of ending on a hard photo edge.
const EDGE_FADE_L = 'linear-gradient(to right, transparent 0, #000 15%)';
const EDGE_FADE_R = 'linear-gradient(to left, transparent 0, #000 15%)';
const FEATHER_LEFT = { WebkitMaskImage: EDGE_FADE_L, maskImage: EDGE_FADE_L };
const FEATHER_RIGHT = { WebkitMaskImage: EDGE_FADE_R, maskImage: EDGE_FADE_R };

// Per-shot width cap. Product/UI shots pass image.maxW so they sit smaller with
// breathing room from the copy; photos/illustrations omit it and stay full-width.
const maxWStyle = (m) => (m ? { maxWidth: typeof m === 'number' ? `${m}px` : m } : {});

const PrimaryCTA = ({ label = 'Apply to join the Founding 50', dark }) => (
  <a
    href="/foundingdesigner"
    className={`group inline-flex items-center gap-2 font-medium text-[15px] rounded-lg px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${
      dark ? 'bg-[#FAF1E2] text-[#1F1812] hover:bg-white font-semibold text-[16px] rounded-md px-9' : 'bg-[#1F1812] text-white hover:bg-[#0F0D0A] hover:shadow-black/20'
    }`}
  >
    {label}
    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
  </a>
);

const WatchDemoLink = ({ className = '' }) => (
  <a href="/how-it-works#demo" data-testid="lp-watch-demo-link" className={`inline-flex items-center gap-1.5 text-[#3A4A32] hover:text-[#233026] text-[15px] font-medium transition-colors ${className}`}>
    Watch Archen™ read a real thread
    <span aria-hidden="true">&rarr;</span>
  </a>
);

const Blocks = ({ blocks, maxW = '52ch' }) => (
  <div className="mt-8 space-y-6 text-[#3D3A36] text-[17px] leading-[1.7]" style={{ maxWidth: maxW }}>
    {blocks.map((b, i) =>
      b.type === 'list' ? (
        <ul key={i} className="space-y-3.5 !mt-2">
          {b.items.map((it, j) => (
            <li key={j} className="flex gap-3">
              <span className="mt-[0.6em] w-1.5 h-1.5 rounded-full bg-[#B79B6E] shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      ) : b.type === 'iconlist' ? (
        <ul key={i} className="space-y-4 !mt-2">
          {b.items.map((it, j) => {
            const Icon = it.icon;
            return (
              <li key={j} className="flex items-center gap-4">
                <span className="w-9 h-9 rounded-full border border-[#E0D6C6] bg-white/60 flex items-center justify-center shrink-0">
                  <Icon size={16} strokeWidth={1.6} className="text-[#7A5A2E]" />
                </span>
                <span className="text-[#1F1812] text-[16px] leading-snug">{it.text}</span>
              </li>
            );
          })}
        </ul>
      ) : b.type === 'iconrow' ? (
        <div key={i} className="!mt-4">
          <div className="flex items-start gap-5 sm:gap-6 flex-wrap">
            {b.items.map((it, j) => {
              const Icon = it.icon;
              return (
                <div key={j} className="flex flex-col items-center text-center w-[80px]">
                  <Icon size={28} strokeWidth={1.3} className="text-[#3A2F26]" />
                  <span className="mt-3 text-[#4A443C] text-[11.5px] leading-snug">{it.text}</span>
                </div>
              );
            })}
            {b.arrow && <ArrowRight size={20} strokeWidth={1.5} className="text-[#B79B6E] self-center mt-1" />}
          </div>
          {b.note && <p className="mt-7 text-[#1F1812]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: '1.35rem', letterSpacing: '0.02em' }}>{b.note}</p>}
        </div>
      ) : b.type === 'pills' ? (
        <div key={i} className="flex flex-nowrap gap-2">
          {b.items.map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 bg-white/70 border border-[#E0D6C6] text-[#3A2F26] text-[12.5px] font-medium rounded-full pl-2.5 pr-3 py-1.5">
              <span className="w-3 h-3 rounded-[4px] border border-[#B79B6E] flex items-center justify-center shrink-0">
                <Check size={8} className="text-[#7A5A2E]" strokeWidth={3} />
              </span>
              {t}
            </span>
          ))}
        </div>
      ) : (
        <div key={i} className={b.links && b.links.length ? 'space-y-3' : undefined}>
          <p className={b.strong ? 'text-[#1F1812] font-medium' : undefined}>{b.text}</p>
          {b.links && b.links.length > 0 && (
            <div className="flex flex-wrap gap-x-6 gap-y-1.5">
              {b.links.map((l) => (
                <a key={l.to} href={l.to} className="inline-flex items-center gap-1.5 text-[#7A5A2E] text-[15px] font-medium underline-offset-4 hover:underline">
                  {l.label}
                  <ArrowRight size={13} strokeWidth={2} />
                </a>
              ))}
            </div>
          )}
        </div>
      )
    )}
  </div>
);

const SectionHead = ({ eyebrow, h2, h2Max, number }) => (
  <>
    {number && (
      <div className="flex items-center gap-4 mb-6">
        <span className="text-[#7A5A2E] text-[12px] tracking-[0.14em] tabular-nums">{number}</span>
        <span className="h-px w-16 bg-[#C9A567]" />
      </div>
    )}
    {eyebrow && <p className={EYEBROW}>{eyebrow}</p>}
    <h2 className={`${eyebrow ? 'mt-6' : ''} text-[#1F1812] leading-[1.1] tracking-[-0.01em]`} style={{ ...H2, fontSize: 'clamp(2rem, 4vw, 3.1rem)', maxWidth: h2Max }}>
      {h2}
    </h2>
  </>
);

const SplitSection = ({ s }) => {
  if (s.stacked) {
    return (
      <section className={`${s.bgColor ? '' : BG[s.bg]} ${SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
        <div className={CONTAINER}>
          <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
          {s.blocks && <Blocks blocks={s.blocks} maxW="62ch" />}
          <div className="mt-10 md:mt-12">
            {s.coded === 'howItWorks'
              ? <HowItWorksSteps />
              : <img src={s.image.src} alt={s.image.alt} className="w-full h-auto block" />}
          </div>
        </div>
      </section>
    );
  }
  if (s.bleedRight && s.textOverlay) {
    return (
      <section className={`relative pb-12 lg:pb-0 ${s.bgColor ? '' : BG[s.bg]}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
        {/* Mobile/tablet: text stacks in-flow above the image (a narrow image can't fit overlaid text) */}
        <div className="lg:hidden px-6 md:px-10 pt-14 pb-8" style={{ paddingLeft: EDGE_GUTTER }}>
          <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
          {s.blocks?.length > 0 && <Blocks blocks={s.blocks} maxW={s.overlayBlocksW || '52ch'} />}
        </div>
        <img
          src={s.image.src}
          alt={s.image.alt}
          className="w-full h-auto block lg:ml-auto ov-bleed-img"
          style={{ '--ov-max': s.image.overlayMaxW || '82%' }}
        />
        <style>{`@media (min-width: 1024px) { .ov-bleed-img { max-width: var(--ov-max); } }`}</style>
        {/* Desktop: absolute text overlay on top of the (now tall enough) image */}
        <div className="hidden lg:flex absolute inset-0 items-center">
          <div className="w-full px-6 md:px-10 lg:pr-0" style={{ paddingLeft: EDGE_GUTTER, maxWidth: `calc(${EDGE_GUTTER} + ${s.overlayTextW || 640}px)` }}>
            <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
            <Blocks blocks={s.blocks} maxW={s.overlayBlocksW || '52ch'} />
          </div>
        </div>
      </section>
    );
  }
  if (s.bleedRight) {
    return (
      <section className={s.bgColor ? '' : BG[s.bg]} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
        <div className="flex flex-col lg:flex-row lg:items-center">
          <div
            className="w-full lg:w-[40%] shrink-0 px-6 md:px-10 py-16 md:py-0"
            style={{ paddingLeft: EDGE_GUTTER }}
          >
            <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
            <Blocks blocks={s.blocks} />
          </div>
          <div className="w-full lg:flex-1 self-stretch">
            <img src={s.image.src} alt={s.image.alt} className="w-full h-auto block" style={s.featherBottom ? BOTTOM_FADE : undefined} />
          </div>
        </div>
      </section>
    );
  }
  if (s.bleedLeft) {
    return (
      <section className={s.bgColor ? '' : BG[s.bg]} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
        <div className="flex flex-col lg:flex-row lg:items-center">
          <div className="w-full lg:flex-1 self-stretch order-2 lg:order-1">
            <img src={s.image.src} alt={s.image.alt} className="w-full h-auto block" style={s.image.featherLeft ? FEATHER_LEFT : undefined} />
          </div>
          <div
            className="w-full lg:w-[42%] shrink-0 px-6 md:px-10 lg:pl-0 py-16 md:py-0 order-1 lg:order-2"
            style={{ paddingRight: EDGE_GUTTER }}
          >
            <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
            <Blocks blocks={s.blocks} />
          </div>
        </div>
      </section>
    );
  }
  const copy = (
    <div className="w-full lg:flex-none" style={{ flexBasis: `${s.textW || DEFAULT_TEXT_W}px` }}>
      <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
      <Blocks blocks={s.blocks} />
    </div>
  );
  const image = (
    <div className={`w-full lg:flex-1 flex justify-center ${s.side === 'left' ? 'lg:justify-start' : 'lg:justify-end'}`}>
      {s.coded === 'extraction' ? (
        <ExtractionFlow />
      ) : (
        <img
          src={s.image.src}
          alt={s.image.alt}
          className={`${s.image.className} ${(s.image.fade || s.image.flat || s.image.featherBottom) ? '' : 'drop-shadow-[0_20px_50px_rgba(60,45,30,0.10)]'}`}
          style={{
            ...maxWStyle(s.image.maxW),
            ...(s.image.fade ? IMAGE_FADE : (s.image.featherBottom ? BOTTOM_FADE : {})),
          }}
        />
      )}
    </div>
  );
  return (
    <section className={`${s.bgColor ? '' : BG[s.bg]} ${s.pb ? `pt-20 md:pt-28 ${s.pb}` : SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
      <div className={CONTAINER}>
        <div className={`flex flex-col ${s.side === 'left' ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 ${s.gap || 'lg:gap-4'} items-center`}>
          {copy}
          {image}
        </div>
      </div>
    </section>
  );
};

const ProofSection = ({ s }) => (
  <section className={`${BG[s.bg]} ${SECTION}`}>
    <div className={CONTAINER}>
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <p className={EYEBROW}>{s.eyebrow}</p>
          <h2 className="mt-6 text-[#1F1812] leading-[1.08] tracking-[-0.01em]" style={{ ...H2, fontSize: 'clamp(2.2rem, 4vw, 3.4rem)', maxWidth: s.h2Max }}>{s.h2}</h2>
          <Blocks blocks={s.blocks} maxW="48ch" />
        </div>
        <div className="flex flex-col justify-end">
          <div className="grid grid-cols-3 gap-6 sm:gap-8">
            {s.stats.map((stat) => (
              <div key={stat.value}>
                <p className="text-[#7A5A2E]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(4.2rem, 5.6vw, 5.6rem)', lineHeight: 1 }}>
                  {stat.value}<span className="text-[0.5em] align-top ml-0.5">%</span>
                </p>
                <p className="mt-3 text-[#3D3A36] text-[15px] leading-snug max-w-[13ch]">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 border-l border-[#C9A567] pl-6 max-w-[58ch]">
            {s.note.text && <p className="text-[#3D3A36] text-[14px] leading-[1.7]">{s.note.text}</p>}
            <a href={s.note.href} className="mt-3 inline-flex items-center gap-2 text-[#7A5A2E] text-[14px] font-medium hover:underline underline-offset-4">
              {s.note.linkLabel}
              <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CardsSection = ({ s }) => (
  <section className={`${BG[s.bg]} ${SECTION}`}>
    <div className={CONTAINER}>
      <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {s.cards.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={i} className="flex flex-col rounded-xl border border-[#E8DFD1] bg-gradient-to-b from-[#FDFAF5] to-[#F7F0E6] p-4 transition-all duration-300 hover:shadow-lg hover:shadow-black/5 hover:-translate-y-0.5">
              <div className="flex items-center gap-1.5 pb-3 border-b border-[#EAE1D3]">
                <Icon size={13} className="text-[#7A5A2E]" strokeWidth={1.7} />
                <span className="text-[#7A5A2E] text-[9.5px] tracking-[0.12em] uppercase">{c.category}</span>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 bg-[#E7EEE0] text-[#5B7247] text-[10.5px] rounded-full px-2 py-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#6E8B4F]" />
                  Captured
                </span>
                <span className="text-[#6B655C] text-[10.5px]">{c.date}</span>
              </div>
              <h3 className="mt-3.5 text-[#1F1812] text-[15px] font-semibold leading-snug">{c.title}</h3>
              <p className="mt-2.5 text-[#6B655C] text-[12.5px] leading-relaxed flex-1">{c.body}</p>
              <div className="mt-4 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full text-[9.5px] font-semibold flex items-center justify-center shrink-0" style={{ backgroundColor: c.avatar, color: c.ink }}>{c.initials}</span>
                <span className="text-[#6B655C] text-[11px]">{c.source}</span>
              </div>
            </div>
          );
        })}
      </div>
      {s.note && (
        <div className="mt-10 border-l border-[#C9A567] pl-6 max-w-[72ch]">
          <p className="text-[#3D3A36] text-[15px] leading-[1.7]">{s.note.text}</p>
          {s.note.linkLabel && (
            <a href={s.note.href} className="mt-3 inline-flex items-center gap-2 text-[#7A5A2E] text-[14px] font-medium hover:underline underline-offset-4">
              {s.note.linkLabel}
              <ArrowRight size={14} />
            </a>
          )}
        </div>
      )}
    </div>
  </section>
);

const boldPercents = (text) =>
  text.split(/(\d+%)/g).map((part, i) => (/^\d+%$/.test(part) ? <strong key={i} className="text-[#1F1812] font-semibold">{part}</strong> : part));

const CalloutBox = ({ callout }) => {
  const Icon = callout.icon;
  return (
    <div className="mt-10 flex items-start gap-5 rounded-2xl border border-[#E4DAC9] bg-[#FBF6EF] px-6 py-6 max-w-[540px]">
      {Icon && <Icon size={30} strokeWidth={1.4} className="text-[#7A5A2E] shrink-0 mt-0.5" />}
      <div>
        <p className="text-[#3D3A36] text-[15px] leading-[1.7]">{boldPercents(callout.text)}</p>
        {callout.sources && <p className="mt-2 text-[#3D3A36] text-[14px] leading-[1.7]">{callout.sources}</p>}
        {callout.linkLabel && (
          <a href={callout.href} className="mt-3 inline-flex items-center gap-2 text-[#7A5A2E] text-[14px] font-semibold hover:underline underline-offset-4">
            {callout.linkLabel}
            <ArrowRight size={14} />
          </a>
        )}
      </div>
    </div>
  );
};

const TextSectionBody = ({ s, wrap }) => (
  <div className={wrap}>
    <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
    {s.blocks && s.blocks.length > 0 && <Blocks blocks={s.blocks} maxW="62ch" />}
    {s.callout && <CalloutBox callout={s.callout} />}
    {s.note && (
      <div className="mt-8 border-l border-[#C9A567] pl-6 max-w-[64ch]">
        {s.note.text && <p className="text-[#3D3A36] text-[14px] leading-[1.7]">{s.note.text}</p>}
        {s.note.linkLabel && (
          <a href={s.note.href} className="mt-3 inline-flex items-center gap-2 text-[#7A5A2E] text-[14px] font-medium hover:underline underline-offset-4">
            {s.note.linkLabel}
            <ArrowRight size={14} />
          </a>
        )}
      </div>
    )}
  </div>
);

const TextSection = ({ s }) => {
  if (s.image && s.imgBleedRight) {
    return (
      <section className={`${s.bgColor ? '' : BG[s.bg]} ${SECTION} overflow-hidden`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
        <div className={CONTAINER}>
          <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-14">
            <div className="w-full order-1 lg:flex-none" style={{ flexBasis: `${s.textW || 520}px` }}>
              <TextSectionBody s={s} wrap="" />
            </div>
            <div className="w-full order-2 lg:flex-1" style={{ marginRight: `calc(-1 * ${EDGE_GUTTER})` }}>
              <img src={s.image.src} alt={s.image.alt} className="w-full h-auto" style={maxWStyle(s.image.maxW)} />
            </div>
          </div>
        </div>
      </section>
    );
  }
  return s.image ? (
    <section className={`${s.bgColor ? '' : BG[s.bg]} ${(s.pt || s.pb) ? `${s.pt || 'pt-20 md:pt-28'} ${s.pb || 'pb-20 md:pb-28'}` : SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
      <div className={CONTAINER}>
        <div className={`grid ${s.imgWide ? (s.side === 'right' ? 'lg:grid-cols-[0.85fr_1.25fr]' : 'lg:grid-cols-[1.25fr_0.85fr]') : 'lg:grid-cols-2'} gap-10 ${s.gap || 'lg:gap-16'} items-center`}>
          <div className={`order-2 flex justify-center ${s.side === 'right' ? 'lg:order-2 lg:justify-end' : 'lg:order-1 lg:justify-start'}`}>
            <img src={s.image.src} alt={s.image.alt} className="w-full h-auto" style={{ maxWidth: s.image.maxW || '900px', ...(s.image.fade ? IMAGE_FADE : {}) }} />
          </div>
          <div className={s.side === 'right' ? 'order-1 lg:order-1' : 'order-1 lg:order-2'}>
            <TextSectionBody s={s} wrap="" />
          </div>
        </div>
      </div>
    </section>
  ) : (
    <section className={`${BG[s.bg]} ${SECTION}`}>
      <div className={CONTAINER}>
        <TextSectionBody
          s={s}
          wrap={
            s.center
              ? 'mx-auto text-center max-w-[720px] [&_h2]:mx-auto'
              : s.side === 'right'
              ? 'lg:ml-auto lg:max-w-[640px]'
              : ''
          }
        />
      </div>
    </section>
  );
};

const INVITE_SPARKLES = [
  { top: '13%', left: '9%', size: 18, delay: '0s' },
  { top: '24%', left: '87%', size: 14, delay: '0.9s' },
  { top: '68%', left: '7%', size: 13, delay: '1.6s' },
  { top: '80%', left: '91%', size: 17, delay: '0.4s' },
  { top: '44%', left: '95%', size: 11, delay: '2.1s' },
  { top: '58%', left: '4%', size: 12, delay: '1.2s' },
  { top: '9%', left: '62%', size: 12, delay: '2.6s' },
];

export const InviteSection = ({ s }) => (
  <section className="relative overflow-hidden bg-[#233026] px-4 md:px-8 py-5 md:py-7">
    <div className="relative border border-[#6E7C68] px-6 py-20 md:px-10 md:py-28 text-center">
      {INVITE_SPARKLES.map((p, i) => (
        <Sparkle key={i} size={p.size} strokeWidth={1.1} fill="#C9A873"
          className="animate-twinkle absolute text-[#C9A873]"
          style={{ top: p.top, left: p.left, animationDelay: p.delay }} />
      ))}
      <div className="animate-invite-glow pointer-events-none absolute left-1/2 top-1/2 h-[440px] w-[640px] rounded-full"
        style={{ background: 'radial-gradient(ellipse at center, rgba(201,168,115,0.16), transparent 70%)' }} />
      <div className="relative mx-auto max-w-[920px] animate-fade-in-up">
        <Leaf size={24} strokeWidth={1.4} className="mx-auto text-[#C9A873]" />
        <p className="mt-5 text-[#C9A873] text-[11px] tracking-[0.2em] uppercase">{s.eyebrow}</p>
        <h2 className="mt-6 mx-auto text-[#F4EEDF] leading-[1.08] tracking-[-0.01em]" style={{ ...H2, fontSize: 'clamp(2.3rem, 4.8vw, 3.8rem)', maxWidth: s.h2Max }}>{s.h2}</h2>
        <div className="mx-auto mt-7 mb-8 h-px w-16 bg-[#6E7C68]" />
        <div className="mx-auto max-w-[720px] space-y-4 text-[#D6CFC0] text-[17px] leading-[1.7]">
          {s.blocks.map((b, i) => <p key={i}>{b.text}</p>)}
        </div>
        <div className="mt-10 flex justify-center"><PrimaryCTA dark /></div>
        {s.footnote && <p className="mt-8 text-[#9AA391] text-[11px] tracking-[0.16em] uppercase">{s.footnote}</p>}
      </div>
    </div>
  </section>
);

const FaqSection = ({ s }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: s.items.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
  return (
    <section className={`${BG[s.bg]} ${SECTION}`}>
      <div className={CONTAINER}>
        <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
        <dl className="mt-12 grid gap-y-0 max-w-[820px]">
          {s.items.map(({ q, a }, i) => (
            <details key={i} className="group border-t border-[#EAE1D3] py-6 last:border-b">
              <summary className="flex items-center justify-between gap-6 cursor-pointer list-none [&::-webkit-details-marker]:hidden text-[#1F1812] text-[19px] md:text-[21px]" style={H2}>
                {q}
                <ArrowRight size={18} className="shrink-0 text-[#7A5A2E] rotate-90 transition-transform duration-200 group-open:-rotate-90" />
              </summary>
              <p className="mt-4 text-[#3D3A36] text-[16px] md:text-[17px] leading-[1.7] max-w-[64ch]">{a}</p>
            </details>
          ))}
        </dl>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      </div>
    </section>
  );
};

const DefinitionSection = ({ s }) => {
  const bg = s.blendTo
    ? { background: `linear-gradient(to bottom, ${s.bgColor} 0%, ${s.bgColor} 40%, ${s.blendTo} 100%)` }
    : s.bgColor
    ? { backgroundColor: s.bgColor }
    : undefined;
  return (
    <section className={`${s.bgColor || s.blendTo ? '' : BG[s.bg]} py-8 md:py-10`} style={bg}>
      <div className={CONTAINER}>
        <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-12 items-center">
          <div>
            <div className="flex items-center gap-4">
              <span className="text-[#7A5A2E] text-[12px] tracking-[0.14em] tabular-nums">{s.number}</span>
              <span className="h-px w-16 bg-[#C9A567]" />
            </div>
            <h2 className="mt-6 text-[#1F1812] leading-[1.02] tracking-[-0.01em]" style={{ ...H2, fontSize: 'clamp(2.4rem, 5vw, 3.6rem)' }}>{s.term}</h2>
            <p className="mt-1 text-[#6B655C] italic" style={{ ...H2, fontSize: '1.35rem' }}>{s.pos}</p>
            <div className="mt-6 mb-7 h-px w-full max-w-[420px] bg-[#EAE1D3]" />
            <p className="text-[#3D3A36] text-[16px] md:text-[17px] leading-[1.75] max-w-[52ch]">{s.def}</p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <img src={s.image.src} alt={s.image.alt} className="w-full h-auto" style={{ maxWidth: '860px' }} />
          </div>
        </div>
      </div>
    </section>
  );
};

const CompareSection = ({ s }) => (
  <section className={`${s.bgColor ? '' : BG[s.bg]} ${SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
    <div className={CONTAINER}>
      <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
      <div className="mt-12 relative border border-[#E7DDCB] rounded-3xl bg-white/40 p-8 md:p-14 grid md:grid-cols-2 gap-12 md:gap-24">
        {s.columns.map((col, ci) => (
          <div key={ci}>
            <p className="text-[#1F1812] text-[12px] font-semibold tracking-[0.16em] uppercase">{col.label}</p>
            <p className="mt-3 text-[#6B655C] text-[15px] leading-[1.6] max-w-[34ch]">{col.sub}</p>
            <ul className="mt-7 space-y-4">
              {col.items.map((it, ii) => (
                <li key={ii} className="flex items-center gap-3.5">
                  {it.ok
                    ? <Check size={18} strokeWidth={2} className="text-[#7A5A2E] shrink-0" />
                    : <X size={18} strokeWidth={2} className="text-[#B7A990] shrink-0" />}
                  <span className="text-[#3D3A36] text-[16px]">{it.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <div className="hidden md:flex absolute left-[41%] top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white border border-[#E7DDCB] items-center justify-center">
          <span className="text-[#1F1812] text-[13px] font-semibold uppercase leading-none" style={{ paddingLeft: '3px' }}>vs.</span>
        </div>
      </div>
    </div>
  </section>
);

const BgImageSection = ({ s }) => {
  const hex = s.bgColor || '#FDF8F3';
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const rgba = (a) => `rgba(${r},${g},${b},${a})`;
  const left = s.side === 'left';
  const dir = left ? 'to left' : 'to right';
  const scrim = s.scrim || `linear-gradient(${dir}, ${hex} 0%, ${hex} 30%, ${rgba(0.82)} 42%, ${rgba(0.32)} 52%, ${rgba(0)} 60%)`;
  return (
    <section className="relative overflow-hidden" style={{ backgroundColor: hex }}>
      <div className="absolute inset-0 z-0 hidden lg:block">
        <img src={s.image.src} alt={s.image.alt} className="absolute top-0 h-full w-auto max-w-none" style={{ objectPosition: s.image.pos || (left ? 'left center' : 'right center'), [left ? 'left' : 'right']: s.image.inset || 0 }} />
        <div className="absolute inset-0" style={{ background: scrim }} />
      </div>
      <div className={`relative z-10 ${CONTAINER} ${SECTION}`}>
        <div className={`max-w-[560px] ${left ? 'lg:ml-auto' : ''}`}>
          <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
          <Blocks blocks={s.blocks} maxW="48ch" />
        </div>
      </div>
    </section>
  );
};

const MatrixSection = ({ s }) => (
  <section className={`${s.bgColor ? '' : BG[s.bg]} ${SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
    <div className={CONTAINER}>
      {(s.eyebrow || s.h2) && (
        <div className="mx-auto text-center max-w-[980px] mb-14 [&_h2]:mx-auto">
          <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
        </div>
      )}
      <ComparisonMatrix columns={s.columns} />
    </div>
  </section>
);

const PlatformCompareSection = ({ s }) => (
  <section className={`${s.bgColor ? '' : BG[s.bg]} ${SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
    <div className={CONTAINER}>
      <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
      {s.blocks?.length > 0 && <Blocks blocks={s.blocks} maxW="70ch" />}
      <div className="mt-12">
        <PlatformComparisonTable columns={s.columns} rowData={s.rowData} />
      </div>
      {(s.footnote || s.note) && (
        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          {s.footnote && <p className="text-[#8B7355] text-[12.5px] leading-[1.6] max-w-[440px]">{s.footnote}</p>}
          {s.note && (
            <div className="flex items-end gap-3 md:max-w-[380px] md:ml-auto">
              <svg width="46" height="40" viewBox="0 0 46 40" fill="none" className="text-[#7A5A2E] shrink-0 mb-1" aria-hidden="true">
                <path d="M4 4 C6 20, 16 30, 40 32" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none" />
                <path d="M32 27 L41 32 L34 39" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
              </svg>
              <p className="text-[#3A342C] text-[19px] leading-[1.4] -rotate-1" style={{ fontFamily: "'Caveat', cursive" }}>
                {s.note}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  </section>
);

const IconsSection = ({ s }) => (  <section className={`${s.bgColor ? '' : BG[s.bg]} ${SECTION}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
    <div className={CONTAINER}>
      <div className="flex flex-col lg:flex-row lg:items-stretch divide-y lg:divide-y-0 lg:divide-x divide-[#E4DAC9]">
        <div className="flex flex-col justify-center lg:w-[26%] lg:pr-9 pb-8 lg:pb-0">
          <div className="flex items-center gap-4 mb-5">
            <span className="text-[#7A5A2E] text-[12px] tracking-[0.14em] tabular-nums">{s.number}</span>
            <span className="h-px w-12 bg-[#C9A567]" />
          </div>
          <h2 className="text-[#1F1812] leading-[1.12] tracking-[-0.01em]" style={{ ...H2, fontSize: 'clamp(1.8rem, 2.8vw, 2.5rem)', maxWidth: s.h2Max }}>{s.h2}</h2>
        </div>
        {s.items.map((it, i) => {
          const Icon = it.icon;
          return (
            <div key={i} className="flex flex-col items-center justify-center text-center lg:flex-1 px-4 py-8 lg:py-4">
              <Icon size={30} strokeWidth={1.3} className="text-[#3A2F26]" />
              <p className="mt-4 text-[#4A443C] text-[13.5px] leading-snug max-w-[16ch]">{it.label}</p>
            </div>
          );
        })}
        <div className="flex items-center lg:w-[26%] lg:pl-9 pt-8 lg:pt-0">
          <p className="text-[#1F1812] leading-[1.18]" style={{ ...H2, fontSize: 'clamp(1.45rem, 2vw, 1.95rem)' }}>{s.closing}</p>
        </div>
      </div>
    </div>
  </section>
);

// Focused, non-generic conversion push for definitional/top-of-funnel pages
// (e.g. LP-014), placed after the last numbered section and before the
// universal Founding 50 invite so readers get a specific next step first.
const EndCtaSection = ({ s }) => (
  <section className={`${BG[s.bg]} ${SECTION}`}>
    <div className={CONTAINER}>
      <div className="rounded-3xl border border-[#E7DDCB] bg-white/70 px-8 py-14 md:px-16 md:py-16 text-center">
        <p className={EYEBROW}>{s.eyebrow}</p>
        <h2 className="mt-6 mx-auto text-[#1F1812] leading-[1.1] tracking-[-0.01em]" style={{ ...H2, fontSize: 'clamp(1.9rem, 3.4vw, 2.7rem)', maxWidth: s.h2Max }}>{s.h2}</h2>
        <p className="mt-5 mx-auto text-[#3D3A36] text-[16px] leading-[1.7] max-w-[54ch]">{s.body}</p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-6">
          <Link
            to={s.primaryTo}
            data-testid="end-cta-primary"
            className="group inline-flex items-center gap-2 bg-[#1F1812] text-white text-[14px] font-medium px-8 py-4 rounded-lg transition-all duration-300 hover:bg-[#0F0D0A] hover:shadow-xl hover:shadow-black/20"
          >
            {s.primaryLabel}
            <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
          <Link
            to={s.secondaryTo}
            data-testid="end-cta-secondary"
            className="text-[#7A5A2E] hover:text-[#1F1812] text-[14px] font-medium underline-offset-4 hover:underline transition-colors"
          >
            {s.secondaryLabel}
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const LandscapeSection = ({ s }) => (
  <section className={`${s.bgColor ? '' : BG[s.bg]} ${padY(s)}`} style={s.bgColor ? { backgroundColor: s.bgColor } : undefined}>
    <div className={CONTAINER}>
      <SectionHead eyebrow={s.eyebrow} h2={s.h2} h2Max={s.h2Max} number={s.number} />
      <div className="mt-14 flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-4">
        <div className="lg:w-[236px] shrink-0">
          {s.lead && <p className="text-[#6B655C] text-[15px] leading-[1.6] max-w-[26ch] mb-8">{s.lead}</p>}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-y-5 gap-x-6">
            {s.sources.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="flex items-center gap-3">
                  <Icon size={22} strokeWidth={1.4} className="text-[#3A2F26] shrink-0" />
                  <span className="text-[#3D3A36] text-[14px] leading-tight">{it.text}</span>
                </div>
              );
            })}
          </div>
          {s.note && <p className="mt-9 text-[#1F1812]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: '1.35rem', letterSpacing: '0.01em' }}>{s.note}</p>}
        </div>

        <div className="hidden lg:block relative shrink-0" style={{ width: 132, height: 150 }}>
          <span className="absolute left-[54%] -translate-x-1/2 z-10 px-1.5 text-[#7A5A2E] text-[10.5px] tracking-[0.16em] uppercase" style={{ top: 30, backgroundColor: s.bgColor || '#F5EFE6' }}>Archen™</span>
          <svg viewBox="0 0 132 150" className="w-full h-full" fill="none">
            <path d="M0 14 C 40 14, 50 75, 68 75" stroke="#C9A567" strokeWidth="1" opacity="0.7" />
            <path d="M0 54 C 42 60, 54 75, 68 75" stroke="#C9A567" strokeWidth="1" opacity="0.7" />
            <path d="M0 96 C 42 90, 54 75, 68 75" stroke="#C9A567" strokeWidth="1" opacity="0.7" />
            <path d="M0 136 C 40 136, 50 75, 68 75" stroke="#C9A567" strokeWidth="1" opacity="0.7" />
            <circle cx="71" cy="75" r="3.5" fill="#C9A567" />
            <path d="M75 75 H126" stroke="#C9A567" strokeWidth="1" />
            <path d="M120 70 L127 75 L120 80" stroke="#C9A567" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        <div className="lg:flex-1 min-w-0">
          {s.recordTitle && (
            <div className="mb-6">
              {s.recordTitle.map((line, i) => (
                <p key={i} className="text-[#1F1812] leading-[1.12]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(1.5rem, 2.1vw, 2rem)' }}>{line}</p>
              ))}
            </div>
          )}
          <img src={s.image.src} alt={s.image.alt} className="w-full h-auto" />
        </div>
      </div>
    </div>
  </section>
);

const RENDERERS = { split: SplitSection, proof: ProofSection, cards: CardsSection, text: TextSection, faq: FaqSection, definition: DefinitionSection, compare: CompareSection, bgimage: BgImageSection, icons: IconsSection, landscape: LandscapeSection, invite: InviteSection, matrix: MatrixSection, platformCompare: PlatformCompareSection, archenFit: ArchenFitSection, endCta: EndCtaSection };

// ── HERO SYSTEM ───────────────────────────────────────────────────────────────
// Per-page-type heroes so sub-pages don't all share one template.
// hero.type: undefined/'split' (default, supports flip), 'editorial', 'photo',
// 'steps', 'guide'.

const HeroSplit = ({ hero }) => {
  const flip = hero.flip;
  const scrim = hero.scrim || (flip
    ? 'linear-gradient(to left, #FDF8F3 0%, #FDF8F3 24%, rgba(253,248,243,0.88) 42%, rgba(253,248,243,0.52) 68%, rgba(253,248,243,0.22) 100%)'
    : 'linear-gradient(to right, #FDF8F3 0%, #FDF8F3 24%, rgba(253,248,243,0.88) 42%, rgba(253,248,243,0.52) 68%, rgba(253,248,243,0.22) 100%)');
  const text = (
    <div className="animate-[lpRise_0.8s_ease-out_both]">
      <p className={EYEBROW}>{hero.eyebrow}</p>
      <h1 className="mt-6 text-[#1F1812] leading-[1.06] tracking-[-0.02em]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.3rem, 4.2vw, 3.55rem)', maxWidth: hero.h1Max }}>
        {hero.h1}
      </h1>
      <p className="mt-8 text-[#3D3A36] text-[18px] md:text-[20px] leading-[1.6] max-w-[44ch]">{hero.sub}</p>
      <div className="mt-10"><PrimaryCTA /></div>
    </div>
  );
  const media = (
    <div className={`relative hidden lg:flex justify-center items-center ${flip ? 'lg:-ml-24' : 'lg:-mr-24'} animate-[lpRise_0.9s_ease-out_0.1s_both]`}>
      {hero.diagram
        ? <img src={hero.diagram} alt={hero.diagramAlt} className="w-full" style={{ maxWidth: hero.diagramMaxW || '920px', ...IMAGE_FADE }} />
        : <HeroDiagram />}
    </div>
  );
  return (
    <section className="relative min-h-screen overflow-hidden bg-[#FDF8F3]">
      <div className="absolute inset-0 z-0">
        <img src={hero.interior || IMG_HERO_INTERIOR} alt={hero.interiorAlt} className="w-full h-full object-cover" style={{ objectPosition: hero.interiorPos || (flip ? '22% center' : '78% center'), transform: hero.interiorFlipX ? 'scaleX(-1)' : undefined }} />
        <div className="absolute inset-0" style={{ background: scrim }} />
      </div>
      <div className="relative z-10 max-w-[1240px] mx-auto px-6 md:px-10 min-h-screen flex items-center pt-32 md:pt-40 pb-20 md:pb-28">
        <div className={`grid ${flip ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)]' : 'lg:grid-cols-[minmax(0,480px)_minmax(0,1fr)]'} gap-10 lg:gap-14 items-center w-full`}>
          {flip ? <>{media}{text}</> : <>{text}{media}</>}
        </div>
      </div>
    </section>
  );
};

const HeroEditorial = ({ hero }) => (
  <section className={`relative overflow-hidden bg-[#FDF8F3] pt-36 md:pt-44 ${hero.pb || 'pb-16 md:pb-20'}`}>
    <div className="max-w-[1040px] mx-auto px-6 md:px-10 text-center animate-[lpRise_0.8s_ease-out_both]">
      {hero.eyebrow && <p className={EYEBROW}>{hero.eyebrow}</p>}
      <h1 className={`${hero.eyebrow ? 'mt-6' : ''} mx-auto text-[#1F1812] leading-[1.05] tracking-[-0.02em]`} style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.6rem, 5.4vw, 4.6rem)', maxWidth: hero.h1Max || '20ch' }}>
        {hero.h1}
      </h1>
      {hero.sub && <p className="mt-7 mx-auto text-[#3D3A36] text-[18px] md:text-[20px] leading-[1.6] max-w-[58ch]">{hero.sub}</p>}
      <div className="mt-9 flex justify-center"><PrimaryCTA /></div>
      {hero.image && <img src={hero.image.src} alt={hero.image.alt} className={`${hero.imgMt || 'mt-12'} w-full lg:w-[122%] lg:-ml-[11%] max-w-none h-auto`} />}
    </div>
    {hero.diagram && (
      <div className="mt-14 md:mt-16 max-w-[1240px] mx-auto px-6 md:px-10 animate-[lpRise_0.95s_ease-out_0.1s_both]">
        <img src={hero.diagram} alt={hero.diagramAlt} className="w-full" style={{ maxWidth: hero.diagramMaxW || '1300px', margin: '0 auto', ...IMAGE_FADE }} />
      </div>
    )}
  </section>
);

const HeroPhoto = ({ hero }) => (
  <>
    <section className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img src={hero.photo} alt={hero.interiorAlt} className="w-full h-full object-cover" style={{ objectPosition: hero.photoPos || 'center' }} />
        {/* Cream scrim: solid-left for the text column (ink text stays AA), soft top band for nav legibility, photo left free on the right. */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #FDF8F3 0%, #FDF8F3 34%, rgba(253,248,243,0.92) 50%, rgba(253,248,243,0.4) 68%, rgba(253,248,243,0) 88%)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(253,248,243,0.8) 0%, rgba(253,248,243,0.1) 15%, transparent 28%)' }} />
      </div>
      <div className="relative z-10 max-w-[1240px] mx-auto px-6 md:px-10 min-h-screen flex items-end pt-40 pb-24 md:pb-32">
        <div className="max-w-[640px] animate-[lpRise_0.8s_ease-out_both]">
          <p className={EYEBROW}>{hero.eyebrow}</p>
          <h1 className="mt-6 text-[#1F1812] leading-[1.05] tracking-[-0.02em]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.4rem, 4.6vw, 3.9rem)', maxWidth: hero.h1Max }}>
            {hero.h1}
          </h1>
          <p className="mt-6 text-[#3D3A36] text-[18px] md:text-[20px] leading-[1.6] max-w-[42ch]">{hero.sub}</p>
          <div className="mt-9"><PrimaryCTA /></div>
        </div>
      </div>
    </section>
    {hero.diagram && (
      <section className="bg-[#FDF8F3] py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 animate-[lpRise_0.9s_ease-out_both]">
          <img src={hero.diagram} alt={hero.diagramAlt} className="w-full" style={{ maxWidth: hero.diagramMaxW || '1100px', margin: '0 auto', ...IMAGE_FADE }} />
        </div>
      </section>
    )}
  </>
);

const HeroSteps = ({ hero }) => (
  <section className="relative overflow-hidden bg-[#FDF8F3] pt-36 md:pt-44 pb-16 md:pb-20">
    <div className="max-w-[1240px] mx-auto px-6 md:px-10">
      <div className="max-w-[820px] animate-[lpRise_0.8s_ease-out_both]">
        <p className={EYEBROW}>{hero.eyebrow}</p>
        <h1 className="mt-6 text-[#1F1812] leading-[1.06] tracking-[-0.02em]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.3rem, 4.2vw, 3.55rem)', maxWidth: hero.h1Max }}>
          {hero.h1}
        </h1>
        <p className="mt-7 text-[#3D3A36] text-[18px] md:text-[20px] leading-[1.6] max-w-[52ch]">{hero.sub}</p>
        <div className="mt-10"><PrimaryCTA /></div>
      </div>
      <div className="mt-14 md:mt-16 animate-[lpRise_0.95s_ease-out_0.1s_both]">
        <HowItWorksSteps />
      </div>
    </div>
  </section>
);

const HeroGuide = ({ hero, sections }) => {
  const toc = (sections || []).filter((s) => s.h2).map((s, i) => ({ id: `sec-${i}`, label: s.h2 }));
  return (
    <section className="relative overflow-hidden bg-[#FDF8F3] pt-36 md:pt-44 pb-14 md:pb-16">
      <div className="max-w-[760px] mx-auto px-6 md:px-10 text-center animate-[lpRise_0.8s_ease-out_both]">
        <p className={EYEBROW}>{hero.eyebrow}</p>
        <h1 className="mt-6 mx-auto text-[#1F1812] leading-[1.08] tracking-[-0.02em]" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 'clamp(2.2rem, 4vw, 3.4rem)', maxWidth: hero.h1Max }}>
          {hero.h1}
        </h1>
        <p className="mt-7 mx-auto text-[#3D3A36] text-[18px] leading-[1.65] max-w-[60ch]">
          {Array.isArray(hero.sub) ? hero.sub.map((line, i) => <span key={i} className="block">{line}</span>) : hero.sub}
        </p>
        <div className="mt-9 flex justify-center"><PrimaryCTA /></div>
      </div>
      {toc.length > 0 && (
        <div className="mt-14 max-w-[720px] mx-auto px-6 md:px-10 animate-[lpRise_0.95s_ease-out_0.1s_both]">
          <div className="rounded-2xl border border-[#E8DFD1] bg-[#FBF6EF] px-7 py-6">
            <p className="text-[#7A5A2E] text-[11px] tracking-[0.2em] uppercase mb-4">In this guide</p>
            <ol className="space-y-2.5">
              {toc.map((t, i) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} className="group flex items-baseline gap-3 text-[#3D3A36] hover:text-[#1F1812] transition-colors">
                    <span className="text-[#B79B6E] text-[13px] tabular-nums w-5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-[16px] leading-snug" style={{ fontFamily: SERIF, fontWeight: 500 }}>{t.label}</span>
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
};

const HEROES = { editorial: HeroEditorial, photo: HeroPhoto, steps: HeroSteps, guide: HeroGuide };
const HeroRouter = ({ hero, sections }) => {
  const H = HEROES[hero.type];
  return H ? <H hero={hero} sections={sections} /> : <HeroSplit hero={hero} />;
};

export const LandingTemplate = ({ config, portalException }) => {
  useEffect(() => {
    if (!config) return;
    document.title = config.seo.title;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute('content', config.seo.description);
    setCanonical(config.route);
    setRobotsMeta(DEFERRED.has(config.id) ? 'noindex, follow' : 'index, follow');
    const leaf = config.seo.title.replace(/\s*\|\s*Archen™\s*$/, '');
    const removeCrumb = setBreadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: leaf, path: config.route },
    ]);
    return () => removeCrumb();
  }, [config]);

  if (!config) return <Navigate to="/" replace />;
  const { hero, sections, closing } = config;

  return (
    <div className="relative min-h-screen bg-[#FDF8F3]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Header />
      <ReadingProgressBar />
      <main>
        {/* HERO */}
        <HeroRouter hero={hero} sections={sections} />

        <section className="bg-[#E9EDE3] border-y border-[#D3D9C7]">
          <div className={`${CONTAINER} py-4 flex items-center justify-center text-center`}>
            <WatchDemoLink />
          </div>
        </section>

        {sections.map((s, i) => {
          const R = RENDERERS[s.type];
          return R ? <div id={`sec-${i}`} key={i} style={{ scrollMarginTop: '96px' }}><R s={s} /></div> : null;
        })}

        {/* CLOSING: [Related links row] -> [green Founding 50 invitation] -> footer */}
        {closing.related?.length > 0 && (
          <section className="bg-[#FDF8F3] border-t border-[#EDE4D6]">
            <div className={`${CONTAINER} py-5 flex flex-wrap items-center gap-x-8 gap-y-2`}>
              <span className="text-[#7A5A2E] text-[11px] tracking-[0.18em] uppercase">Related</span>
              <div className="flex flex-wrap gap-x-7 gap-y-2 text-[#5C4F42] text-[14px]">
                {closing.related.map((r) => (
                  <a key={r.to} href={r.to} className="hover:text-[#1F1812] transition-colors">{r.label}</a>
                ))}
              </div>
            </div>
          </section>
        )}

        <InviteSection s={{
          eyebrow: 'Founding 50',
          h2: 'You are invited to help shape what comes next.',
          h2Max: '30ch',
          blocks: portalException
            ? [
                { text: 'The self-updating client portal is coming first to the Founding 50.' },
                { text: 'Fifty seats. Join now to get it first and help shape it before it ships.' },
              ]
            : [
                { text: 'Fifty founding firms. A direct line to the team, a real say in the roadmap, and the first look at everything new, the self-updating client portal included.' },
                { text: 'Fifty seats. Applications are open now.' },
              ],
          footnote: '50 founding firms · Direct access to the team · A voice in what we build next',
        }} />
      </main>
      <Footer />
      <style>{`@keyframes lpRise { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

// Hero image constants (shared across all pages).
const IMG_HERO_INTERIOR = 'https://customer-assets-v7afamib.emergentagent.net/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/zyj70rou_image.webp';
const IMG_HERO_DIAGRAM = 'https://customer-assets-v7afamib.emergentagent.net/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/xhln8qc7_Group%202.webp';

// Route wrapper: /mockup/:lpId -> looks up config by id. Graduated ids (now
// live at their clean route + in the sitemap) redirect immediately so old
// /mockup/ links and bookmarks land on the one canonical URL. This is a
// client-side redirect (SPA has no server-level redirect config available in
// this hosting setup); paired with the canonical tag it's the SPA-equivalent
// of a 301 but is not a true HTTP 301 status.
const LandingTemplateRoute = () => {
  const { lpId } = useParams();
  if (GRADUATED[lpId]) return <Navigate to={GRADUATED[lpId]} replace />;
  return <LandingTemplate config={getLandingPage(lpId)} portalException={lpId === 'lp-005'} />;
};

export default LandingTemplateRoute;
